import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CustomTileData } from 'features/layout/features/config/types';
import { CustomTileContent } from 'features/layout/tiles/CustomTileContent';
import { CustomTileHostRegistry } from 'features/layout/tiles/custom-tile-host/registry';
import { placeHostBeforePaint } from 'features/layout/tiles/custom-tile-host/useCustomTileGeometrySync';
import { useIsCustomTileMaximized } from 'features/layout/tiles/useIsCustomTileMaximized';
import { useHoverParent } from 'features/layout/useHoverParent';

// The maximized tile is its own hover parent, the way ContentTile marks it in the grid.
const MAXIMIZED_HOVER_PARENT_CLASS = 'hover-parent-movement-on';

// Inline, not emotion: the PiP window copies the stylesheets once when it opens, so a class first
// rendered inside it has no rules.
const HOST_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  visibility: 'hidden',
  pointerEvents: 'none',
};

type CustomTileHostProps = {
  data: CustomTileData;
  registry: CustomTileHostRegistry;
  hoverParent: ReturnType<typeof useHoverParent>;
};

export const CustomTileHost = ({ data, registry, hoverParent }: CustomTileHostProps) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const holdingHoverRef = useRef(false);

  const [placed, setPlaced] = useState(false);

  const maximized = useIsCustomTileMaximized(data.name);
  const { className, showHover, resetAutoHideTimer, cancelAutoHideTimer, isShowingHover } =
    hoverParent;

  useLayoutEffect(() => {
    if (!hostRef.current) {
      return undefined;
    }

    const unregister = registry.registerHost(data.name, hostRef.current);

    placeHostBeforePaint(registry.slots.get(data.name), hostRef.current);
    setPlaced(true);

    return unregister;
  }, [registry, data.name]);

  // The integrator's iframe swallows every mousemove, so the countdown can never be reset from
  // inside the tile. Hold the chrome open for as long as the pointer is in here.
  const holdHoverOpen = useCallback(() => {
    holdingHoverRef.current = true;

    if (!isShowingHover()) {
      showHover();
    }

    cancelAutoHideTimer();
  }, [isShowingHover, showHover, cancelAutoHideTimer]);

  const releaseHover = useCallback(() => {
    if (!holdingHoverRef.current) {
      return;
    }

    holdingHoverRef.current = false;
    resetAutoHideTimer();
  }, [resetAutoHideTimer]);

  // Maximizing drops the handlers below, and an unmount removes them outright; either would strand
  // a cancelled countdown and leave the whole tile strip's chrome on screen for good. The cleanup
  // covers both, which is why `maximized` is a dependency at all.
  useEffect(() => releaseHover, [maximized, releaseHover]);

  return (
    <div
      ref={hostRef}
      style={HOST_STYLE}
      className={maximized ? MAXIMIZED_HOVER_PARENT_CLASS : className}
      // A maximized host answers for itself through :hover, the way ContentTile does. Reporting
      // to the tiles area from there would keep the whole sidebar's chrome awake.
      {...(maximized
        ? {}
        : { onMouseEnter: holdHoverOpen, onMouseMove: holdHoverOpen, onMouseLeave: releaseHover })}
    >
      {placed ? <CustomTileContent data={data} /> : null}
    </div>
  );
};
